import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import authService from '../services/auth';
import { getUserFromToken } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const checkAuthStatus = useCallback(async () => {
    const authenticated = authService.isAuthenticated();
    const token = authService.getToken();

    setIsLoggedIn(authenticated);

    if (authenticated && token) {
      try {
        // Try to get fresh user data from API
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        // Fallback to token data if API call fails
        const tokenUserData = getUserFromToken(token);
        setUser(tokenUserData);
      }
    } else {
      setUser(null);
    }

    setLoading(false);
  }, []);

  const login = useCallback((authToken, refreshToken) => {
    authService.setTokens(authToken, refreshToken);
    checkAuthStatus();
  }, [checkAuthStatus]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authService.logout();
    } catch (error) {
      // Proceed with logout even if API call fails
    } finally {
      setUser(null);
      setIsLoggedIn(false);
      setLoading(false);
      // Redirect to login page after logout
      window.location.href = '/login?message=LoggedOut';
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      await authService.refreshToken();
      await checkAuthStatus();
      return true;
    } catch (error) {
      logout();
      return false;
    }
  }, [checkAuthStatus, logout]);

  useEffect(() => {
    checkAuthStatus();

    // Listen for storage changes (logout in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === 'refreshToken') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuthStatus]);

  const value = {
    user,
    isLoggedIn,
    loading,
    login,
    logout,
    refreshToken,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
